// Carli's confidence in an upload's estimated recovery. The score is driven by
// how much of the submission she could actually model: estimate coverage,
// identifier and MSRP completeness, how concentrated the assortment is, and
// how much of the invoice actually arrived.

export type ConfidenceInput = {
  received: number
  lineItems?: number
  msrp?: number
  estimatedRecovery?: number
  missingPredict?: number
  missingMsrp?: number
  missingUpc?: number
  shortShipped?: number
}

export type ConfidenceFactor = {
  label: string
  detail: string
  // Share of this factor's weight that was earned, 0–1.
  score: number
}

export type ConfidenceResult = {
  score: number
  label: 'High' | 'Medium' | 'Limited'
  factors: ConfidenceFactor[]
}

const pct = (value: number) => `${Math.round(value * 100)}%`

export function scoreConfidence(input: ConfidenceInput): ConfidenceResult | null {
  const lines = input.lineItems ?? 0
  // Without a valued line-level file there is nothing to score.
  if (lines <= 0 || !input.estimatedRecovery || input.estimatedRecovery <= 0) return null

  const estimateCoverage = (lines - (input.missingPredict ?? 0)) / lines
  const msrpCoverage = (lines - (input.missingMsrp ?? 0)) / lines
  const upcCoverage = (lines - (input.missingUpc ?? 0)) / lines
  const depth = input.received / lines
  // Deeper size runs per line make demand easier to predict; ~8 units per line
  // is treated as a healthy run.
  const depthScore = Math.min(1, depth / 8)
  const fulfillment = input.received > 0 ? input.received / (input.received + (input.shortShipped ?? 0)) : 0

  const factors: ConfidenceFactor[] = [
    {
      label: 'Estimate coverage',
      detail: `${(lines - (input.missingPredict ?? 0)).toLocaleString('en-US')} of ${lines.toLocaleString('en-US')} lines carry a per-unit estimate`,
      score: estimateCoverage,
    },
    {
      label: 'MSRP completeness',
      detail: `${pct(msrpCoverage)} of lines have a retail price to anchor against`,
      score: msrpCoverage,
    },
    {
      label: 'Identifier coverage',
      detail: `${pct(upcCoverage)} of lines carry a barcode for sales matching`,
      score: upcCoverage,
    },
    {
      label: 'Assortment depth',
      detail: `${depth.toFixed(1)} units per line across ${lines.toLocaleString('en-US')} lines`,
      score: depthScore,
    },
    {
      label: 'Receipt integrity',
      detail: (input.shortShipped ?? 0) > 0
        ? `${(input.shortShipped ?? 0).toLocaleString('en-US')} invoiced units never arrived`
        : 'Every invoiced unit was received',
      score: fulfillment,
    },
  ]

  const weights = [0.35, 0.2, 0.15, 0.15, 0.15]
  const weighted = factors.reduce((sum, factor, index) => sum + factor.score * weights[index], 0)
  // Held below 100 — a forecast on unsold inventory is never certain.
  const score = Math.round(Math.min(94, Math.max(35, weighted * 100)))

  return {
    score,
    label: score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Limited',
    factors,
  }
}
